import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider, AuthProvider, useAuth } from './context/AppContext';
import Layout from './components/layout/Layout';
import Login from './pages/auth/Login';
import SuperDashboard from './pages/superadmin/Dashboard';
import Panels from './pages/superadmin/Panels';
import Admins from './pages/superadmin/Admins';
import PermissionTypes from './pages/superadmin/PermissionTypes';
import AdminDashboard from './pages/admin/Dashboard';
import Roles from './pages/admin/Roles';
import Employees from './pages/admin/Employees';
import AdminModules from './pages/admin/Modules';
import ApiKeys from './pages/superadmin/ApiKeys';
import './styles/global.css';

function ProtectedRoute({ children, allow }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg-base)' }}>
      <div style={{ textAlign:'center' }}>
        <span className="spinner" style={{ width:36, height:36, margin:'0 auto' }} />
        <p style={{ marginTop:12, color:'var(--text-muted)', fontSize:'.875rem' }}>Loading…</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(user.userType)) {
    if (user.userType === 'superadmin') return <Navigate to="/superadmin/dashboard" replace />;
    if (user.userType === 'admin' || user.userType === 'subadmin') return <Navigate to="/admin/dashboard" replace />;
    return <Navigate to="/login" replace />;
  }
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={user.userType==='superadmin' ? '/superadmin/dashboard' : '/admin/dashboard'} replace /> : <Login />} />

      {/* Super Admin */}
      <Route path="/superadmin/*" element={
        <ProtectedRoute allow={['superadmin']}>
          <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Routes>
              <Route path="dashboard"         element={<SuperDashboard />} />
              <Route path="panels"            element={<Panels />} />
              <Route path="admins"            element={<Admins />} />
              <Route path="permission-types"  element={<PermissionTypes />} />
              <Route path="api-keys"          element={<ApiKeys />} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Admin */}
      <Route path="/admin/*" element={
        <ProtectedRoute allow={['admin', 'subadmin']}>
          <Layout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
            <Routes>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="modules"   element={<AdminModules />} />
              <Route path="roles"     element={<Roles />} />
              <Route path="employees" element={<Employees />} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Routes>
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/" element={
        user ? <Navigate to={user.userType==='superadmin' ? '/superadmin/dashboard' : '/admin/dashboard'} replace />
             : <Navigate to="/login" replace />
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
                fontFamily: 'var(--font-body)',
                fontSize: '.875rem',
              },
              success: { iconTheme: { primary: 'var(--success)', secondary: 'var(--bg-card)' } },
              error:   { iconTheme: { primary: 'var(--danger)',  secondary: 'var(--bg-card)' } },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
